import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
  SafeAreaView,
  StatusBar,
  Dimensions,
} from "react-native";
import { ScrollView } from "react-native-gesture-handler";
import Icon from "react-native-vector-icons/Feather";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllNotifications,
  readNotification,
  readAllNotifications,
  getAllNotificationsCount,
  deleteUserNotification,
} from "../../../redux/dispatcher/notificationDispatcher";
import { useToast } from "react-native-toast-notifications";
import { useIsFocused, useNavigation } from "@react-navigation/native";
import {
  updatePage,
  updateSort,
  updateStatus,
} from "../../../redux/reducers/notificationReducer";

const { width: screenWidth } = Dimensions.get("window");

// Responsive breakpoints
const isSmallScreen = screenWidth < 760;
const isMediumScreen = screenWidth >= 1080 && screenWidth < 760;

// Responsive values
const getResponsiveValue = (small: number, medium: number, large: number) => {
  if (isSmallScreen) return small;
  if (isMediumScreen) return medium;
  return large;
};

const UserSettingNotificationScreen = () => {
  const navigation = useNavigation();
  const [refreshing, setRefreshing] = useState(false);
  const [isCompactView, setIsCompactView] = useState(screenWidth < 414);

  const dispatch = useDispatch<any>();
  const toast = useToast();

  const {
    loadingActionNotification,
    loading,
    notifications,
    totalCount,
    totalPages,
    pageSize,
    page,
    sort,
    status,
  } = useSelector((state: any) => state.notification);
  const isFocused = useIsFocused();

  // Handle screen rotation
  useEffect(() => {
    const updateLayout = () => {
      const { width } = Dimensions.get("window");
      setIsCompactView(width < 414);
    };

    const subscription = Dimensions.addEventListener("change", updateLayout);
    return () => subscription?.remove();
  }, []);

  useEffect(() => {
    if (isFocused) {
      fetchNotifications();
    }
  }, [isFocused, sort, status, pageSize, page, totalCount, totalPages]);

  const fetchNotifications = async () => {
    dispatch(
      getAllNotifications({
        page: page,
        pageSize: pageSize,
        sort: { key: "createdAt", direction: "desc" },
        status: status,
      }),
    );
    dispatch(getAllNotificationsCount());
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchNotifications();
    } finally {
      setRefreshing(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();

    if (now - date < 24 * 60 * 60 * 1000) {
      const hours = Math.floor((now - date) / (60 * 60 * 1000));
      if (hours < 1) {
        const minutes = Math.floor((now - date) / (60 * 1000));
        return `${minutes}m ago`;
      }
      return `${hours}h ago`;
    }

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "success":
        return { name: "check-circle", color: "#10B981" };
      case "info":
        return { name: "info", color: "#3B82F6" };
      case "warning":
        return { name: "alert-triangle", color: "#F59E0B" };
      case "alert":
        return { name: "alert-circle", color: "#EF4444" };
      default:
        return { name: "bell", color: "#6B7280" };
    }
  };

  const handleSort = (key: any) => {
    let direction = "asc";
    if (sort.key === key && sort.direction === "asc") {
      direction = "desc";
    }
    dispatch(updateSort({ key: key, direction: direction }));
  };

  const markAsRead = (id: any) => {
    dispatch(readNotification(id))
      .unwrap()
      .then(() => fetchNotifications())
      .catch(() =>
        toast.show("Error updating notification", { type: "danger" }),
      );
  };

  const deleteNotification = (id: any) => {
    dispatch(deleteUserNotification(id))
      .then(() => fetchNotifications())
      .catch(() =>
        toast.show("Error deleting notification", { type: "danger" }),
      );
  };

  const markAllAsRead = () => {
    dispatch(readAllNotifications())
      .then(() => fetchNotifications())
      .catch(() =>
        toast.show("Error updating notifications", { type: "danger" }),
      );
  };

  const handleNavigation = (item: any) => {
    const type = item.notificationType;

    if (type === "ITEM_TRACKER_ENQUIRY") {
      navigation.navigate("Root", {
        screen: "Home",
        params: {
          screen: "Tracker",
          params: {
            screen: "ProductTrackerDetailsReport",
            params: { order: item?.referencedData },
          },
        },
      });
    } else if (type === "CHINA_TRACKER_ENQUIRY") {
      navigation.navigate("Root", {
        screen: "Home",
        params: {
          screen: "Tracker",
          params: {
            screen: "ChinaTrackerDetailsReport",
            params: { order: item?.referencedData },
          },
        },
      });
    } else if (type.startsWith("FACTORY_")) {
      navigation.navigate("Root", {
        screen: "Home",
        params: {
          screen: "Tracker",
          params: {
            screen: "ChinaTrackerDetailsReport",
            params: { order: item?.referencedData },
          },
        },
      });
    } else if (type === "OPERATION_ORDERS") {
      navigation.navigate("Root", {
        screen: "Home",
        params: {
          screen: "Operations",
          params: { screen: "ManageOperationsScreen" },
        },
      });
    } else if (type === "PORTAL_ISSUES") {
      navigation.navigate("Root", {
        screen: "Home",
        params: {
          screen: "Customer",
          params: { screen: "CustomerIssueReport" },
        },
      });
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      dispatch(updatePage(newPage));
    }
  };

  const SortIcon = ({ column }: any) => {
    const iconName =
      sort.key === column
        ? sort.direction === "asc"
          ? "chevron-up"
          : "chevron-down"
        : "minus";

    const iconColor = sort.key === column ? "#3B82F6" : "#9CA3AF";

    return (
      <Icon
        name={iconName}
        size={getResponsiveValue(12, 14, 16)}
        color={iconColor}
      />
    );
  };

  // Card View for smaller screens
  const NotificationCard = ({ item }: any) => {
    const icon = getNotificationIcon(item.notificationType);

    return (
      <View
        style={[styles.notificationCard, !item.isRead && styles.unreadCard]}
      >
        <View style={styles.cardHeader}>
          <View style={styles.cardIcon}>
            <Icon name={icon.name} size={20} color={icon.color} />
            {!item.isRead && <View style={styles.unreadDot} />}
          </View>
          <View style={styles.cardHeaderText}>
            <Text style={styles.cardType}>
              {item.notificationType.replace(/_/g, " ")}
            </Text>
            <Text style={styles.cardDate}>{formatDate(item.createdAt)}</Text>
          </View>
        </View>

        <TouchableOpacity
          style={styles.cardContent}
          onPress={() => !item.isRead && markAsRead(item._id)}
        >
          <Text
            style={[
              styles.cardMessage,
              !item.isRead && styles.unreadCardMessage,
            ]}
            numberOfLines={3}
          >
            {item.message}
          </Text>
        </TouchableOpacity>

        <View style={styles.cardActions}>
          <TouchableOpacity
            style={styles.cardActionButton}
            onPress={() => handleNavigation(item)}
          >
            <Icon name="external-link" size={16} color="#3B82F6" />
            <Text style={styles.cardActionText}>View</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.cardActionButton}
            onPress={() => deleteNotification(item._id)}
          >
            <Icon name="trash-2" size={16} color="#EF4444" />
            <Text style={[styles.cardActionText, { color: "#EF4444" }]}>
              Delete
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  // Table View for larger screens
  const DataTableHeader = () => (
    <View style={styles.tableHeader}>
      <View style={styles.statusColumn}>
        <Text style={styles.headerText}>Status</Text>
      </View>
      <TouchableOpacity style={styles.messageColumn}>
        <Text style={styles.headerText}>Message</Text>
      </TouchableOpacity>
      <TouchableOpacity style={styles.typeColumn}>
        <Text style={styles.headerText}>Type</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={styles.dateColumn}
        onPress={() => handleSort("createdAt")}
      >
        <Text style={styles.headerText}>Date</Text>
        <SortIcon column="createdAt" />
      </TouchableOpacity>
      <View style={styles.actionsColumn}>
        <Text style={styles.headerText}>Actions</Text>
      </View>
    </View>
  );

  const DataTableRow = ({ item, index }: any) => {
    const icon = getNotificationIcon(item.notificationType);
    const isEven = index % 2 === 0;

    return (
      <View style={[styles.tableRow, isEven && styles.evenRow]}>
        <View style={styles.statusColumn}>
          <View style={styles.statusIndicator}>
            <Icon name={icon.name} size={16} color={icon.color} />
            {!item.isRead && <View style={styles.unreadDot} />}
          </View>
        </View>

        <TouchableOpacity
          style={styles.messageColumn}
          onPress={() => !item.isRead && markAsRead(item._id)}
        >
          <Text
            style={[
              styles.messageText,
              !item.isRead && styles.unreadMessageText,
            ]}
            numberOfLines={2}
          >
            {item.message}
          </Text>
        </TouchableOpacity>

        <View style={styles.typeColumn}>
          <Text style={styles.typeText}>
            {item.notificationType.replace(/_/g, " ")}
          </Text>
        </View>

        <View style={styles.dateColumn}>
          <Text style={styles.dateText}>{formatDate(item.createdAt)}</Text>
        </View>

        <View style={styles.actionsColumn}>
          <View style={styles.actionButtons}>
            <TouchableOpacity
              style={styles.actionButton}
              onPress={() => handleNavigation(item)}
            >
              <Icon name="external-link" size={14} color="#3B82F6" />
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.actionButton}
              onPress={() => deleteNotification(item._id)}
            >
              <Icon name="trash-2" size={14} color="#EF4444" />
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  };

  const PaginationControls = () => {
    const startPage = Math.max(1, page - 2);
    const endPage = Math.min(totalPages, startPage + 4);
    const pages = [];

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <View style={styles.paginationContainer}>
        <View style={styles.paginationInfo}>
          <Text style={styles.paginationText}>
            Showing {(page - 1) * pageSize + 1} to{" "}
            {Math.min(page * pageSize, totalCount)} of {totalCount} entries
          </Text>
        </View>

        <View style={styles.paginationControls}>
          <TouchableOpacity
            style={[
              styles.paginationButton,
              page === 1 && styles.disabledButton,
            ]}
            onPress={() => handlePageChange(page - 1)}
            disabled={page === 1}
          >
            <Icon
              name="chevron-left"
              size={16}
              color={page === 1 ? "#9CA3AF" : "#374151"}
            />
          </TouchableOpacity>

          {pages.map((pageNum) => (
            <TouchableOpacity
              key={pageNum}
              style={[
                styles.paginationButton,
                page === pageNum && styles.activePage,
              ]}
              onPress={() => handlePageChange(pageNum)}
            >
              <Text
                style={[
                  styles.paginationButtonText,
                  page === pageNum && styles.activePageText,
                ]}
              >
                {pageNum}
              </Text>
            </TouchableOpacity>
          ))}

          <TouchableOpacity
            style={[
              styles.paginationButton,
              page === totalPages && styles.disabledButton,
            ]}
            onPress={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
          >
            <Icon
              name="chevron-right"
              size={16}
              color={page === totalPages ? "#9CA3AF" : "#374151"}
            />
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="dark-content" backgroundColor="#FFFFFF" />

      {/* Header */}
      <View style={styles.header}>
        <View style={{ flexDirection: "row", alignItems: "center", flex: 1 }}>
          {isSmallScreen && (
            <TouchableOpacity
              onPress={() => navigation.navigate("SettingsM")}
              style={styles.backButton}
            >
              <Icon name="arrow-left" size={18} color="#2563EB" />
            </TouchableOpacity>
          )}
          <View style={styles.headerLeft}>
            <Text style={styles.title}>Notifications</Text>
            <Text style={styles.subtitle}>{totalCount} total</Text>
          </View>
        </View>

        <View style={styles.headerActions}>
          <TouchableOpacity
            style={styles.refreshButton}
            onPress={handleRefresh}
            disabled={refreshing}
          >
            <Icon
              name="refresh-cw"
              size={getResponsiveValue(16, 18, 20)}
              color="#6B7280"
            />
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.filterButton}
            onPress={() =>
              dispatch(
                updateStatus(
                  status === "all"
                    ? "unread"
                    : status === "unread"
                      ? "read"
                      : "all",
                ),
              )
            }
          >
            <Icon
              name="filter"
              size={getResponsiveValue(14, 16, 18)}
              color="#6B7280"
            />
            {!isCompactView && (
              <Text style={styles.filterText}>
                {status === "all"
                  ? "All"
                  : status === "read"
                    ? "Read"
                    : "Unread"}
              </Text>
            )}
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.markAllButton}
            onPress={markAllAsRead}
          >
            <Icon
              name="check-circle"
              size={getResponsiveValue(14, 16, 18)}
              color="#FFFFFF"
            />
            {!isCompactView && (
              <Text style={styles.markAllText}>Mark All Read</Text>
            )}
          </TouchableOpacity>
        </View>
      </View>

      {/* Content */}
      <View style={styles.contentContainer}>
        {loading || loadingActionNotification ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#3B82F6" />
            <Text style={styles.loadingText}>Loading notifications...</Text>
          </View>
        ) : (
          <>
            {notifications.length === 0 ? (
              <View style={styles.emptyState}>
                <Icon name="bell-off" size={48} color="#D1D5DB" />
                <Text style={styles.emptyTitle}>No notifications found</Text>
                <Text style={styles.emptySubtitle}>
                  {status !== "all"
                    ? `No ${status} notifications to display`
                    : "You're all caught up!"}
                </Text>
              </View>
            ) : (
              <>
                {isCompactView ? (
                  // Card View for smaller screens
                  <ScrollView style={styles.cardContainer}>
                    {notifications?.map((item: any, index: number) => (
                      <NotificationCard
                        key={item._id}
                        item={item}
                        index={index}
                      />
                    ))}
                  </ScrollView>
                ) : (
                  // Table View for larger screens
                  <View style={styles.tableContainer}>
                    <DataTableHeader />
                    <ScrollView style={styles.tableBody}>
                      {notifications?.map((item: any, index: number) => (
                        <DataTableRow
                          key={item._id}
                          item={item}
                          index={index}
                        />
                      ))}
                    </ScrollView>
                  </View>
                )}
              </>
            )}

            {totalPages > 1 && <PaginationControls />}
          </>
        )}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F9FAFB",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: getResponsiveValue(16, 20, 24),
    paddingVertical: getResponsiveValue(12, 16, 20),
    backgroundColor: "#FFFFFF",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  headerLeft: {
    flex: 1,
  },
  backButton: {
    width: 38,
    height: 38,
    borderRadius: 12,
    backgroundColor: "#FFFFFF",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 3,
    elevation: 1,
  },
  title: {
    fontSize: getResponsiveValue(24, 28, 32),
    fontWeight: "700",
    color: "#111827",
    marginBottom: 4,
  },
  subtitle: {
    fontSize: getResponsiveValue(12, 14, 16),
    color: "#6B7280",
  },
  headerActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: getResponsiveValue(8, 12, 16),
  },
  refreshButton: {
    padding: getResponsiveValue(6, 8, 10),
    borderRadius: 8,
    backgroundColor: "#F3F4F6",
  },
  filterButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: getResponsiveValue(8, 12, 16),
    paddingVertical: getResponsiveValue(6, 8, 10),
    backgroundColor: "#F3F4F6",
    borderRadius: 8,
    gap: 6,
  },
  filterText: {
    color: "#374151",
    fontSize: getResponsiveValue(12, 14, 16),
    fontWeight: "500",
  },
  markAllButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: getResponsiveValue(12, 16, 20),
    paddingVertical: getResponsiveValue(6, 8, 10),
    backgroundColor: "#3B82F6",
    borderRadius: 8,
    gap: 6,
  },
  markAllText: {
    color: "#FFFFFF",
    fontSize: getResponsiveValue(12, 14, 16),
    fontWeight: "600",
  },
  contentContainer: {
    flex: 1,
    margin: getResponsiveValue(12, 16, 20),
  },
  // Card View Styles
  cardContainer: {
    flex: 1,
  },
  notificationCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: getResponsiveValue(12, 16, 20),
    marginBottom: getResponsiveValue(8, 12, 16),
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  unreadCard: {
    borderLeftWidth: 4,
    borderLeftColor: "#3B82F6",
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12,
  },
  cardIcon: {
    position: "relative",
    marginRight: 12,
  },
  cardHeaderText: {
    flex: 1,
  },
  cardType: {
    fontSize: getResponsiveValue(12, 14, 16),
    fontWeight: "600",
    color: "#374151",
    textTransform: "capitalize",
  },
  cardDate: {
    fontSize: getResponsiveValue(10, 12, 14),
    color: "#6B7280",
    marginTop: 2,
  },
  cardContent: {
    marginBottom: 12,
  },
  cardMessage: {
    fontSize: getResponsiveValue(12, 14, 16),
    color: "#374151",
    lineHeight: getResponsiveValue(18, 20, 22),
  },
  unreadCardMessage: {
    fontWeight: "600",
    color: "#111827",
  },
  cardActions: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: "#F1F5F9",
  },
  cardActionButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: getResponsiveValue(8, 12, 16),
    paddingVertical: getResponsiveValue(6, 8, 10),
    backgroundColor: "#F3F4F6",
    borderRadius: 8,
    gap: 6,
  },
  cardActionText: {
    fontSize: getResponsiveValue(12, 14, 16),
    color: "#3B82F6",
    fontWeight: "500",
  },
  // Table View Styles
  tableContainer: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  tableHeader: {
    flexDirection: "row",
    paddingHorizontal: getResponsiveValue(16, 20, 24),
    paddingVertical: getResponsiveValue(12, 16, 20),
    backgroundColor: "#F8FAFC",
    borderBottomWidth: 1,
    borderBottomColor: "#E2E8F0",
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  tableBody: {
    flex: 1,
  },
  tableRow: {
    flexDirection: "row",
    paddingHorizontal: getResponsiveValue(16, 20, 24),
    paddingVertical: getResponsiveValue(12, 16, 20),
    borderBottomWidth: 1,
    borderBottomColor: "#F1F5F9",
    alignItems: "center",
  },
  evenRow: {
    backgroundColor: "#FAFBFC",
  },
  statusColumn: {
    flex: 0.6,
    alignItems: "center",
  },
  messageColumn: {
    flex: 2.5,
    paddingRight: 8,
  },
  typeColumn: {
    flex: 1,
  },
  dateColumn: {
    flex: 1,
  },
  actionsColumn: {
    flex: 1,
    alignItems: "center",
  },
  headerText: {
    fontSize: getResponsiveValue(10, 12, 14),
    fontWeight: "600",
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  statusIndicator: {
    position: "relative",
    alignItems: "center",
    justifyContent: "center",
  },
  unreadDot: {
    position: "absolute",
    top: -2,
    right: -2,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: "#EF4444",
  },
  messageText: {
    fontSize: getResponsiveValue(12, 14, 16),
    color: "#374151",
    lineHeight: getResponsiveValue(16, 20, 24),
    flexShrink: 1,
    flexWrap: "wrap",
  },
  unreadMessageText: {
    fontWeight: "600",
    color: "#111827",
  },
  typeText: {
    fontSize: getResponsiveValue(10, 12, 14),
    color: "#6B7280",
    textTransform: "capitalize",
  },
  dateText: {
    fontSize: getResponsiveValue(10, 12, 14),
    color: "#6B7280",
  },
  actionButtons: {
    flexDirection: "row",
    gap: 8,
  },
  actionButton: {
    padding: getResponsiveValue(4, 6, 8),
    borderRadius: 6,
    backgroundColor: "#F3F4F6",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 60,
  },
  loadingText: {
    marginTop: 12,
    fontSize: getResponsiveValue(12, 14, 16),
    color: "#6B7280",
  },
  emptyState: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 80,
  },
  emptyTitle: {
    fontSize: getResponsiveValue(16, 18, 20),
    fontWeight: "600",
    color: "#374151",
    marginTop: 16,
  },
  emptySubtitle: {
    fontSize: getResponsiveValue(12, 14, 16),
    color: "#6B7280",
    marginTop: 8,
    textAlign: "center",
    paddingHorizontal: 20,
  },
  paginationContainer: {
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: getResponsiveValue(16, 20, 24),
    paddingVertical: getResponsiveValue(12, 16, 20),
    backgroundColor: "#F8FAFC",
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    borderTopWidth: 1,
    borderTopColor: "#E2E8F0",
  },
  paginationInfo: {},
  paginationText: {
    fontSize: getResponsiveValue(10, 12, 14),
    color: "#6B7280",
    textAlign: isSmallScreen ? "center" : "left",
  },
  paginationControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  paginationButton: {
    paddingHorizontal: getResponsiveValue(8, 12, 16),
    paddingVertical: getResponsiveValue(6, 8, 10),
    borderRadius: 6,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E2E8F0",
    minWidth: getResponsiveValue(32, 36, 40),
    alignItems: "center",
  },
  activePage: {
    backgroundColor: "#3B82F6",
    borderColor: "#3B82F6",
  },
  disabledButton: {
    backgroundColor: "#F8FAFC",
    borderColor: "#E2E8F0",
  },
  paginationButtonText: {
    fontSize: getResponsiveValue(10, 12, 14),
    color: "#374151",
    fontWeight: "500",
  },
  activePageText: {
    color: "#FFFFFF",
  },
});

export default UserSettingNotificationScreen;
